import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface ReviewItem {
  id: string;
  job_title: string | null;
  overall_rating: number;
  quality_rating: number | null;
  communication_rating: number | null;
  timeliness_rating: number | null;
  price_fairness_rating: number | null;
  review_text: string | null;
  photo_urls: string[];
  reviewer_display_name: string;
  response_text: string | null;
  responded_at: string | null;
  created_at: string;
}

interface PhotoData {
  id: string;
  caption: string | null;
  url: string;
  uploaded_at: string;
}

interface ProfileData {
  contractor: {
    id: string;
    business_name: string;
    primary_trade: string;
    bio: string | null;
    verified_at: string | null;
    response_badge: string | null;
    avg_response_hours: number | null;
  };
  summary: {
    total_reviews: number;
    average_overall: number;
    average_quality: number | null;
    average_communication: number | null;
    average_timeliness: number | null;
    average_price_fairness: number | null;
  };
  data: ReviewItem[];
  pagination: {
    has_more: boolean;
    next_cursor: string | null;
  };
}

type Filter = 'all' | '5' | '4' | 'low' | 'photos';

const TRADE_LABELS: Record<string, string> = {
  hvac: 'HVAC', plumbing: 'Plumbing', electrical: 'Electrical',
  roofing: 'Roofing', general: 'General Contractor',
  pest_control: 'Pest Control', landscaping: 'Landscaping',
  painting: 'Painting', appliance_repair: 'Appliance Repair', flooring: 'Flooring',
};

function AfterPhotosGallery({ contractorId }: { contractorId: string }) {
  const { data } = useQuery({
    queryKey: ['contractor-photos', contractorId],
    queryFn: () => api.get<{ data: PhotoData[] }>(`/contractor/${contractorId}/photos`),
    enabled: !!contractorId,
  });
  const photos = data?.data ?? [];
  if (photos.length === 0) return null;

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#111827' }}>Work photos</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {photos.map(p => (
          <div key={p.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '1' }}>
            <img
              src={p.url}
              alt={p.caption ?? 'Work photo'}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Stars({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}
    </span>
  );
}

function RatingBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) return null;
  const pct = Math.round((value / 5) * 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{value.toFixed(1)}</span>
      </div>
      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: '#f59e0b', borderRadius: 99,
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: ReviewItem }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111827' }}>
            {review.reviewer_display_name}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>
            {review.job_title && `${review.job_title} · `}
            {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <Stars rating={review.overall_rating} size={16} />
      </div>

      {/* Category micro-ratings */}
      {(review.quality_rating ?? review.communication_rating ?? review.timeliness_rating ?? review.price_fairness_rating) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {([
            ['Quality', review.quality_rating],
            ['Comms', review.communication_rating],
            ['Time', review.timeliness_rating],
            ['Price', review.price_fairness_rating],
          ] as [string, number | null][]).filter(([, v]) => v !== null).map(([label, v]) => (
            <span key={label} style={{
              fontSize: 11, fontWeight: 500, color: '#6b7280',
              background: '#f9fafb', border: '1px solid #f3f4f6',
              borderRadius: 99, padding: '2px 8px',
            }}>
              {label} {'★'.repeat(v!)}
            </span>
          ))}
        </div>
      )}

      {review.review_text && (
        <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151', lineHeight: 1.55 }}>
          {review.review_text}
        </p>
      )}

      {review.photo_urls.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto' }}>
          {review.photo_urls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt=""
              style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
          ))}
        </div>
      )}

      {/* Contractor response */}
      {review.response_text && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe',
          borderRadius: 10, padding: '10px 12px', marginTop: 8,
        }}>
          <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Contractor Response
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
            {review.response_text}
          </p>
        </div>
      )}
    </div>
  );
}

const FILTER_CHIPS: { key: Filter; label: string }[] = [
  { key: 'all',    label: 'All' },
  { key: '5',     label: '5★' },
  { key: '4',     label: '4★' },
  { key: 'low',   label: '3★ and below' },
  { key: 'photos', label: 'Photos' },
];

function filterToParams(f: Filter): Record<string, string> {
  if (f === '5')     return { min_rating: '5', max_rating: '5' };
  if (f === '4')     return { min_rating: '4', max_rating: '4' };
  if (f === 'low')   return { max_rating: '3' };
  if (f === 'photos') return { has_photos: 'true' };
  return {};
}

export function ContractorPublicProfile() {
  const { id: contractorId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<Filter>('all');

  const extraParams = filterToParams(activeFilter);
  const queryString = new URLSearchParams({ limit: '20', ...extraParams }).toString();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['contractor-profile', contractorId, activeFilter],
    queryFn: () => api.get<ProfileData>(`/reviews/contractor/${contractorId}?${queryString}`),
    enabled: !!contractorId,
  });

  if (isLoading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 32, height: 32, border: '3px solid #1D4ED8', borderTopColor: 'transparent',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <p style={{ fontSize: 16, color: '#6b7280', textAlign: 'center' }}>Contractor not found.</p>
        <button onClick={() => navigate(-1)} style={{ marginTop: 16, padding: '10px 20px', background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>
          Go back
        </button>
      </div>
    );
  }

  const { contractor, summary, data: reviews } = data;

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 96 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #1e40af 100%)', padding: '48px 20px 28px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 36, height: 36, borderRadius: 10, cursor: 'pointer',
            fontSize: 18, marginBottom: 16,
          }}
        >
          &#8592;
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 60, height: 60, borderRadius: 16,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, flexShrink: 0,
          }}>
            🔧
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, color: '#fff', fontSize: 20, fontWeight: 700 }}>
                {contractor.business_name}
              </h1>
              {contractor.verified_at && (
                <span style={{
                  background: '#22c55e', color: '#fff',
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>
                  Verified
                </span>
              )}
            </div>
            <span style={{
              display: 'inline-block', marginTop: 4,
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              fontSize: 12, fontWeight: 500, padding: '2px 10px', borderRadius: 99,
            }}>
              {TRADE_LABELS[contractor.primary_trade] ?? contractor.primary_trade}
            </span>
          </div>
        </div>

        {/* Response badge */}
        {contractor.response_badge && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <span style={{
              background: contractor.response_badge === 'lightning' ? '#fef9c3' : contractor.response_badge === 'fast' ? '#eff6ff' : '#f9fafb',
              color: contractor.response_badge === 'lightning' ? '#854d0e' : contractor.response_badge === 'fast' ? '#1d4ed8' : '#374151',
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {contractor.response_badge === 'lightning' ? '⚡ Lightning response' : contractor.response_badge === 'fast' ? '🚀 Fast responder' : '✓ Responsive'}
              {contractor.avg_response_hours != null && ` (avg ${contractor.avg_response_hours}h)`}
            </span>
          </div>
        )}

        {/* Rating hero */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <span style={{ fontSize: 40, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
            {summary.average_overall.toFixed(1)}
          </span>
          <div>
            <Stars rating={summary.average_overall} size={22} />
            <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
              {summary.total_reviews} {summary.total_reviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 16px 0' }}>
        {/* Category rating bars */}
        {(summary.average_quality ?? summary.average_communication ?? summary.average_timeliness ?? summary.average_price_fairness) && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
            <RatingBar label="Quality of work"  value={summary.average_quality} />
            <RatingBar label="Communication"    value={summary.average_communication} />
            <RatingBar label="Timeliness"       value={summary.average_timeliness} />
            <RatingBar label="Price fairness"   value={summary.average_price_fairness} />
          </div>
        )}

        {/* After-photos gallery */}
        <AfterPhotosGallery contractorId={contractorId!} />

        {/* Bio */}
        {contractor.bio && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{contractor.bio}</p>
          </div>
        )}

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
          {FILTER_CHIPS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              style={{
                flexShrink: 0, padding: '6px 14px', borderRadius: 99, border: 'none',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                background: activeFilter === key ? '#1D4ED8' : '#fff',
                color: activeFilter === key ? '#fff' : '#374151',
                boxShadow: activeFilter === key ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Reviews */}
        {reviews.length === 0 && (
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32,
            textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: 14 }}>
              No reviews match this filter.
            </p>
          </div>
        )}
        {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
      </div>

      {/* Fixed request quote button */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '12px 16px 28px', background: '#fff', borderTop: '1px solid #f3f4f6',
      }}>
        <button
          type="button"
          onClick={() => navigate('/app/tasks')}
          style={{
            width: '100%', padding: 14,
            background: '#1D4ED8', color: '#fff',
            border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Request a Quote
        </button>
      </div>
    </div>
  );
}
